import { LogPointsConnectorsState } from './model'
import { AppAction, Dispatch, ExtraArgs, GetState } from '../../../../types'
import { LogPointsService } from '../../util/LogPointsService'
import logPointsSelectors from '../../LogPointsPage/selectors'
import { changePlatformToken, selectors as authSelectors } from '../../../../lifecycle/auth'
import { ExternalValueProps } from './LogPointsApplicationsBody'
import { getComponentsResourceTypeFromResource, getResourceId, Resource } from '../../../../common/resource/model'
import { getLogPointsResourceType, LogPointsType } from '../../LogPointsPage/model'

export enum ActionType {
  FetchLogPointsConnectors = 'Monitoring/LogPoints/FetchLogPointsConnectors',
  FetchedLogPointsConnectors = 'Monitoring/LogPoints/FetchedLogPointsConnectors',
  SavingLogPointsConnectors = 'Monitoring/LogPoints/SavingLogPointsConnectors',
  SavedLogPointsConnectors = 'Monitoring/LogPoints/SavedLogPointsConnectors',
  SavedLogPointsConnectorsError = 'Monitoring/LogPoints/SavedLogPointsConnectorsError',
  ResetConnectorsErrorAndSaved = 'Monitoring/LogPoints/ResetConnectorsErrorAndSaved'
}

// Basic actions

export type FetchLogPointsConnectorsAction = {
  type: ActionType.FetchLogPointsConnectors
}

export type FetchedLogPointsConnectorsAction = {
  type: ActionType.FetchedLogPointsConnectors
  payload: {logPointsConnectors: LogPointsConnectorsState}
}

export type SavingLogPointsConnectorsAction = {
  type: ActionType.SavingLogPointsConnectors
}

export type SavedLogPointsConnectorsAction = {
  type: ActionType.SavedLogPointsConnectors
}

export type SavedLogPointsConnectorsErrorAction = {
  type: ActionType.SavedLogPointsConnectorsError
  payload: string
}

export type ResetConnectorsErrorAndSavedAction = {
  type: ActionType.ResetConnectorsErrorAndSaved
}

export type LogPointsApplicationsAction =
  | FetchLogPointsConnectorsAction
  | FetchedLogPointsConnectorsAction
  | SavingLogPointsConnectorsAction
  | SavedLogPointsConnectorsAction
  | SavedLogPointsConnectorsErrorAction
  | ResetConnectorsErrorAndSavedAction

// Basic Actions Creators

export const fetchLogPointsConnectorsAction = (): AppAction => ({
  type: ActionType.FetchLogPointsConnectors
})

export const fetchedLogPointsConnectorsAction = (logPointsConnectors: LogPointsConnectorsState): AppAction => ({
  type: ActionType.FetchedLogPointsConnectors,
  payload: {logPointsConnectors}
})

export const savingLogPointsConnectorsAction = (): AppAction => ({
  type: ActionType.SavingLogPointsConnectors
})

export const savedLogPointsConnectorsAction = (): AppAction => ({
  type: ActionType.SavedLogPointsConnectors
})

export const savedLogPointsConnectorsErrorAction = (error: string) => ({
  type: ActionType.SavedLogPointsConnectorsError,
  payload: error
})

export const resetConnectorsErrorAndSavedAction = () => ({
  type: ActionType.ResetConnectorsErrorAndSaved
})

// Thunks

export const resetConnectorsErrorAndSaved = () => (dispatch: Dispatch) => {
  dispatch(resetConnectorsErrorAndSavedAction())
}

export const fetchLogPointsConnectors = (environmentId: string, resourceId: string, targetId?: string) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    dispatch(fetchLogPointsConnectorsAction())

    const resourceType = getLogPointsResourceType({environmentId, resourceId, targetId, type: LogPointsType.APPLICATION})
    const connectors = getState().monitoring.logPoints.connectors.resources
    const resources = logPointsSelectors.resourceSelector.getAll(getState()).resources
    const resource = resources.find((res: Resource) =>
      getResourceId(res) === resourceId && getComponentsResourceTypeFromResource(res) === resourceType
    )

    if (connectors && resource) {
      const maybePlatformToken = authSelectors.getPlatformToken(getState())

      if (maybePlatformToken) {
        const orgId = getState().monitoring.auth.profile.organizationId
        const remoteAPIManager = api.apiManager(getState)
        const remoteARM = api.arm(getState)
        const logPointsService = new LogPointsService(remoteAPIManager, remoteARM)

        logPointsService.fetchLogPointsConnectors(orgId, environmentId, connectors, resource, targetId).then(logPointsConnectors => {
          dispatch(fetchedLogPointsConnectorsAction(logPointsConnectors))
        })
      } else {
        const apiPlatformRemoteAPI = api.apiPlatform(getState)
        apiPlatformRemoteAPI.getAPIPlatformToken()
          .then(session => {
            dispatch(changePlatformToken(session.token))
            dispatch(fetchLogPointsConnectors(environmentId, resourceId, targetId))
          })
      }
    }
  }

export const saveLogPointsConnectors = (props: ExternalValueProps, logPointsConnectors: LogPointsConnectorsState) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    dispatch(savingLogPointsConnectorsAction())

    const resourceType = getLogPointsResourceType({
      environmentId: props.environmentId, resourceId: props.resourceId, targetId: props.targetId, type: LogPointsType.APPLICATION
    })
    const resources = logPointsSelectors.resourceSelector.getAll(getState()).resources
    const resource = resources.find((res: Resource) =>
      getResourceId(res) === props.resourceId && getComponentsResourceTypeFromResource(res) === resourceType
    )

    if (resource) {
      const maybePlatformToken = authSelectors.getPlatformToken(getState())

      if (maybePlatformToken) {
        const orgId = getState().monitoring.auth.profile.organizationId
        const remoteAPIManager = api.apiManager(getState)
        const remoteARM = api.arm(getState)
        const logPointsService = new LogPointsService(remoteAPIManager, remoteARM)

        logPointsService.saveLogPointsConnectors(orgId, props.environmentId, resource, logPointsConnectors, props.targetId)
          .then(() => {
            dispatch(fetchedLogPointsConnectorsAction(logPointsConnectors))
            dispatch(savedLogPointsConnectorsAction())
          }).catch(err => dispatch(savedLogPointsConnectorsErrorAction(err)))
      } else {
        const apiPlatformRemoteAPI = api.apiPlatform(getState)
        apiPlatformRemoteAPI.getAPIPlatformToken()
          .then(session => {
            dispatch(changePlatformToken(session.token))
            dispatch(saveLogPointsConnectors(props, logPointsConnectors))
          })
      }
    } else {
      dispatch(savedLogPointsConnectorsErrorAction('Resource is undefined'))
    }
  }
